import { DateTime } from "@bps/date-time";
import {
  dataAttribute,
  DataAttributes,
  Stack,
  Text,
  TextBadgeSize,
  useTheme
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ClaimStatusBadge } from "@modules/acc/screens/shared-components/claim-status-badge/ClaimStatusBadge.tsx";
import { ConsultsRemainingBadges } from "@modules/clinical/screens/patient-record/components/claims/ConsultsRemainingBadges.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { When } from "@ui-components/withPerm.tsx";

import {
  ConditionListItem,
  EpisodeOfCareBadgesLabels
} from "../condition-modal/Condition.types.ts";
import { convertClinicalSideToDiagnosisSide } from "./active-condition-list.utils.ts";
import { getSubheadingStyles } from "./ActiveConditionList.styles.ts";

type ActiveClaimListItemProps = {
  conditionListItem: ConditionListItem;
};

export const ActiveConditionListItem: React.FC<ActiveClaimListItemProps> = ({
  conditionListItem: { episodeOfCare }
}) => {
  const { palette } = useTheme();
  const { core } = useStores();

  const claimAppointmentVisits = episodeOfCare.claim?.appointmentVisits;

  const initialDate = episodeOfCare.claim?.dto.initialConsultDate
    ? `Initial ${DateTime.fromISO(
        episodeOfCare.claim?.dto.initialConsultDate
      ).toDayDefaultFormat()}`
    : `Created ${
        episodeOfCare.createdDate.isToday
          ? "today"
          : episodeOfCare.createdDate.toDayDefaultFormat()
      }`;

  const shouldShowBadges =
    !episodeOfCare.isPrivate &&
    !episodeOfCare.discharged &&
    episodeOfCare.claim &&
    claimAppointmentVisits &&
    claimAppointmentVisits.length > 0;

  return (
    <Stack
      styles={{ root: { color: palette.neutralPrimary } }}
      {...dataAttribute(
        DataAttributes.Data,
        episodeOfCare.claim ? episodeOfCare.claim.id : episodeOfCare.id
      )}
    >
      <Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center" wrap>
        <Text>
          {episodeOfCare.primaryDiagnosis}
          {convertClinicalSideToDiagnosisSide(episodeOfCare.diagnosisSide)}
        </Text>
        <When permission={Permission.ClaimRead}>
          {shouldShowBadges && (
            <ConsultsRemainingBadges
              initialConsultDate={initialDate}
              appointmentVisits={claimAppointmentVisits}
            />
          )}
        </When>
      </Stack>
      <Stack
        horizontal
        tokens={{ childrenGap: 8 }}
        styles={{
          root: {
            marginTop: 4
          }
        }}
      >
        {episodeOfCare.displayDate1 && (
          <Text styles={getSubheadingStyles}>
            {episodeOfCare.displayDate1.label}{" "}
            {episodeOfCare.displayDate1.date.isToday
              ? "today"
              : episodeOfCare.displayDate1.date.toDayDefaultFormat()}
          </Text>
        )}
        {episodeOfCare.displayDate2 && (
          <>
            <Text styles={getSubheadingStyles}>|</Text>
            <Text styles={getSubheadingStyles}>
              {episodeOfCare.displayDate2.label}{" "}
              {episodeOfCare.displayDate2.date.isToday
                ? "today"
                : episodeOfCare.displayDate2.date.toDayDefaultFormat()}
            </Text>
          </>
        )}
        {core.hasPermissions(Permission.ClaimRead) &&
          !episodeOfCare.isPrivate &&
          episodeOfCare.claim?.claimNumber && (
            <>
              <Text styles={getSubheadingStyles}>|</Text>
              <Text
                styles={getSubheadingStyles}
              >{`Claim number ${episodeOfCare.claim.claimNumber}`}</Text>
            </>
          )}
        {!episodeOfCare.isPrivate && (
          <ClaimStatusBadge
            compactMode={true}
            claimId={episodeOfCare.claim?.id}
            badgeSize={TextBadgeSize.small}
            status={episodeOfCare.claim?.claimStatus}
            isDischarged={episodeOfCare.claim?.isDischarged}
          />
        )}
        {core.hasPermissions(Permission.ClaimRead) &&
          episodeOfCare.isPrivate && (
            <>
              <Text styles={getSubheadingStyles}>|</Text>
              <Text styles={getSubheadingStyles}>
                {EpisodeOfCareBadgesLabels.Private}
              </Text>
            </>
          )}
      </Stack>
    </Stack>
  );
};
